import {
    ListItemIcon,
    ListItemText,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import { useIntl } from 'react-intl';
import TonalSelect from '~/components/Inputs/TonalSelect';
import { FIELD_SIZING } from '~/theme/fieldSizing';
import { BooleanOperation } from '~/engine/util/algorithms/sdf/SDFOperations';
import { menuProps } from '../selectMenuProps';
import {
    BOOLEAN_OPERATION_TEXT,
    BOOLEAN_OPERATIONS,
} from './booleanOperations';
import OperationIcon from './OperationIcon';

/**
 * Fixed width in px, fitting the longest operation name, so the structure
 * select beside it lines up across rows. The menu grows to fit descriptions.
 */
const OPERATION_SELECT_WIDTH = 120;

interface OperationSelectProps {
    value: BooleanOperation;
    onChange: (operation: BooleanOperation) => void;
    /** What the operation's two inputs are called in the descriptions. */
    inputs: { left: string; right: string };
}

/**
 * Picks the boolean operation of one step. Each row shows the operation's
 * icon, its name and what it keeps of the step's two inputs, named as
 * the panel names them.
 */
export default function OperationSelect({
    value,
    onChange,
    inputs,
}: OperationSelectProps) {
    const intl = useIntl();
    const name = (operation: BooleanOperation) =>
        intl.formatMessage({ id: BOOLEAN_OPERATION_TEXT[operation].nameId });

    return (
        <TonalSelect
            value={value}
            onChange={(e) => onChange(e.target.value as BooleanOperation)}
            size="small"
            sx={{ width: OPERATION_SELECT_WIDTH, flexShrink: 0 }}
            inputProps={{
                'aria-label': intl.formatMessage({ id: 'selectOperation' }),
            }}
            MenuProps={menuProps}
            renderValue={(operation) => (
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={`${FIELD_SIZING.gap}px`}
                >
                    <OperationIcon operation={operation as BooleanOperation} />
                    <Typography variant="inherit" noWrap flex={1}>
                        {name(operation as BooleanOperation)}
                    </Typography>
                </Stack>
            )}
        >
            {BOOLEAN_OPERATIONS.map((operation) => (
                <MenuItem key={operation} value={operation}>
                    <ListItemIcon>
                        <OperationIcon operation={operation} />
                    </ListItemIcon>
                    <ListItemText
                        primary={name(operation)}
                        secondary={intl.formatMessage(
                            {
                                id: BOOLEAN_OPERATION_TEXT[operation]
                                    .descriptionId,
                            },
                            inputs
                        )}
                    />
                </MenuItem>
            ))}
        </TonalSelect>
    );
}
